import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import "../../styles/common/popup.scss";
import dragIcon from "../../assets/svg/dropIcon.svg";
import { getCourses } from "../../helper/apicalls/course";
import { getAllBatchInstitution } from "../../helper/apicalls/institution/institutionBatch";
import Cookies from "js-cookie";

const CreateVideoPopup = ({
  open,
  handleClose,
  onSave,
  mode = "create",
  video = null,
  preselectedCourse = null,
  preselectedBatch = null,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceType, setSourceType] = useState("youtube");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState("");
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [urlError, setUrlError] = useState("");
  const [courseError, setCourseError] = useState("");
  const [batchError, setBatchError] = useState("");

  const fileInputRef = useRef(null);

  const sourceTypeOptions = [
    { value: "youtube", label: "YouTube URL" },
    { value: "public_url", label: "Public Video URL" },
    { value: "azure", label: "Upload Video" },
  ];

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-bg)",
      borderColor: "var(--border-color)",
      color: "var(--color-text-dark)",
      minHeight: "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "var(--color-primary)",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-bg)",
      zIndex: 10001,
      position: "absolute",
      width: "100%",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10001,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
          ? "var(--color-hover)"
          : "var(--color-bg)",
      color: state.isSelected ? "white" : "var(--color-text-dark)",
      "&:hover": {
        backgroundColor: "var(--color-hover)",
      },
      fontSize: "var(--fs-md)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--color-text-dark)",
      fontSize: "var(--fs-md)",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "var(--fs-md)",
    }),
  };

  const isValidUrl = (value) => {
    if (!value || !value.trim()) return false;
    const trimmed = value.trim();
    const urlRegex =
      /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#\[\]@!$&'()*+,;=]*)?$/;
    return urlRegex.test(trimmed);
  };

  const fetchCoursesAndSetInitials = async () => {
    try {
      const data = await getCourses();
      const normalized = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data) && Array.isArray(data?.[0]?.course)
          ? data[0].course
          : Array.isArray(data)
            ? data
            : [];
      setCourses(normalized);

      if (mode === "edit" && video && video.course) {
        const matchCourse = normalized.find(c => c.id === video.course || c._id === video.course);
        if (matchCourse) {
          setSelectedCourse({ value: matchCourse.id || matchCourse._id, label: matchCourse.name, key: matchCourse.key });
          
          const token = Cookies.get("institution_token");
          const response = await getAllBatchInstitution(token, matchCourse.key, {});
          const batchesNormalized = response?.data || [];
          setBatches(batchesNormalized);
          
          if (video.batch) {
            const matchBatch = batchesNormalized.find(b => b.id === video.batch || b._id === video.batch);
            if (matchBatch) {
              setSelectedBatch({ value: matchBatch.id || matchBatch._id, label: matchBatch.name });
            }
          }
        }
      } else if (preselectedCourse) {
        const matchCourse = normalized.find(c => c.id === preselectedCourse.value || c._id === preselectedCourse.value || c.key === preselectedCourse.key);
        if (matchCourse) {
          setSelectedCourse({ value: matchCourse.id || matchCourse._id, label: matchCourse.name, key: matchCourse.key });
          
          const token = Cookies.get("institution_token");
          const response = await getAllBatchInstitution(token, matchCourse.key, {});
          const batchesNormalized = response?.data || [];
          setBatches(batchesNormalized);
          
          if (preselectedBatch) {
            const matchBatch = batchesNormalized.find(b => b.id === preselectedBatch.value || b._id === preselectedBatch.value);
            if (matchBatch) {
              setSelectedBatch({ value: matchBatch.id || matchBatch._id, label: matchBatch.name });
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching courses in popup:", err);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && video) {
      setTitle(video.title || "");
      setDescription(video.description || "");
      setSourceType(video.source_type || "youtube");
      setUrl(video.video_url || "");
    } else {
      setTitle("");
      setDescription("");
      setSourceType("youtube");
      setUrl("");
    }

    setFile(null);
    setFileError("");
    setSubmitting(false);
    setTitleError("");
    setDescriptionError("");
    setUrlError("");
    setCourseError("");
    setBatchError("");

    if (preselectedCourse) {
      setSelectedCourse(preselectedCourse);
      if (preselectedBatch) {
        setSelectedBatch(preselectedBatch);
      }
    } else {
      setSelectedCourse(null);
      setSelectedBatch(null);
      setBatches([]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    fetchCoursesAndSetInitials();
  }, [open, mode, video, preselectedCourse, preselectedBatch]);

  const handleCourseChange = async (selectedOption) => {
    setSelectedCourse(selectedOption);
    setSelectedBatch(null);
    setBatches([]);
    setCourseError("");
    if (selectedOption) {
      try {
        const token = Cookies.get("institution_token");
        const response = await getAllBatchInstitution(token, selectedOption.key, {});
        setBatches(response?.data || []);
      } catch (err) {
        console.error("Error fetching batches:", err);
      }
    }
  };

  const handleBatchChange = (selectedOption) => {
    setSelectedBatch(selectedOption);
    setBatchError("");
  };

  const isValidVideoFile = (f) => f && f.type && f.type.startsWith("video/");

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!isValidVideoFile(f)) {
      setFileError("Please upload a valid video file");
      return;
    }
    setFileError("");
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const f = e.dataTransfer.files?.[0];
    if (!isValidVideoFile(f)) {
      setFileError("Please upload a valid video file");
      return;
    }
    setFileError("");
    setFile(f);
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasError = false;

    if (!title.trim()) {
      setTitleError("Title is required");
      hasError = true;
    } else {
      setTitleError("");
    }

    if (!description.trim()) {
      setDescriptionError("Description is required");
      hasError = true;
    } else {
      setDescriptionError("");
    }

    if (!selectedCourse) {
      setCourseError("Course is required");
      hasError = true;
    } else {
      setCourseError("");
    }

    if (!selectedBatch) {
      setBatchError("Batch is required");
      hasError = true;
    } else {
      setBatchError("");
    }

    if (sourceType !== "azure") {
      if (!url.trim()) {
        setUrlError("Video URL is required");
        hasError = true;
      } else if (!isValidUrl(url)) {
        setUrlError(
          "Please enter a valid URL (starting with http:// or https://)",
        );
        hasError = true;
      } else {
        setUrlError("");
      }
    } else if (sourceType === "azure" && !file && !url.trim()) {
      setFileError("Please provide a video file or use the existing URL");
      hasError = true;
    } else {
      setFileError("");
    }

    if (hasError) return;
    if (submitting) return;

    setSubmitting(true);

    try {
      let finalData;

      if (sourceType === "azure") {
        if (file) {
          finalData = new FormData();
          finalData.append("title", title.trim());
          finalData.append("description", description.trim());
          finalData.append("source_type", sourceType);
          finalData.append("video_file", file);
          finalData.append("course", selectedCourse.value);
          finalData.append("batch", selectedBatch.value);
        } else {
          finalData = {
            title: title.trim(),
            description: description.trim(),
            source_type: sourceType,
            video_url: url.trim(),
            course: selectedCourse.value,
            batch: selectedBatch.value,
          };
        }
      } else {
        finalData = {
          title: title.trim(),
          description: description.trim(),
          source_type: sourceType,
          video_url: url.trim(),
          course: selectedCourse.value,
          batch: selectedBatch.value,
        };
      }
      console.log(finalData, "logging final data is");

      await onSave?.(finalData, mode, video?.id);
      handleClose();
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="common-popup-overlay high-z-index" onClick={handleClose}>
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <h2>{mode === "edit" ? "Edit Video" : "Upload Video"}</h2>
          <button className="popup-close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="popup-content">
          <div className="popup-form-group">
            <label>Title</label>
            <input
              className="popup-input"
              placeholder="Enter video title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (e.target.value.trim()) {
                  setTitleError("");
                }
              }}
            />
            {titleError && <p className="file-error-text">{titleError}</p>}
          </div>
          <div className="popup-form-group">
            <label>Description</label>
            <textarea
              className="popup-input"
              rows={3}
              placeholder="Enter video description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (e.target.value.trim()) {
                  setDescriptionError("");
                }
              }}
            />
            {descriptionError && (
              <p className="file-error-text">{descriptionError}</p>
            )}
          </div>

          {!(preselectedCourse || mode === "edit") && (
            <div className="popup-form-group">
              <label>Course</label>
              <Select
                className="popup-select-react"
                classNamePrefix="popup-select"
                value={selectedCourse}
                onChange={handleCourseChange}
                options={courses.map(c => ({ value: c.id || c._id, label: c.name, key: c.key }))}
                styles={selectStyles}
                isSearchable={true}
                placeholder="Select Course"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={mode === "edit" || !!preselectedCourse}
              />
              {courseError && <p className="file-error-text">{courseError}</p>}
            </div>
          )}

          {!(preselectedBatch || mode === "edit") && (
            <div className="popup-form-group">
              <label>Batch</label>
              <Select
                className="popup-select-react"
                classNamePrefix="popup-select"
                value={selectedBatch}
                onChange={handleBatchChange}
                options={batches.map(b => ({ value: b.id || b._id, label: b.name }))}
                styles={selectStyles}
                isSearchable={true}
                placeholder="Select Batch"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={!selectedCourse || mode === "edit" || !!preselectedBatch}
              />
              {batchError && <p className="file-error-text">{batchError}</p>}
            </div>
          )}

          <div className="popup-form-group">
            <label>Video Source</label>
            <Select
              className="popup-select-react"
              classNamePrefix="popup-select"
              value={
                sourceTypeOptions.find(
                  (option) => option.value === sourceType,
                ) || null
              }
              onChange={(selectedOption) => {
                if (!selectedOption) return;
                setSourceType(selectedOption.value);
                setUrl("");
                setFile(null);
              }}
              options={sourceTypeOptions}
              styles={selectStyles}
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          {sourceType !== "azure" && (
            <div className="popup-form-group">
              <label>Video URL</label>
              <input
                className="popup-input"
                placeholder="Enter video URL (https://...)"
                value={url}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  setUrl(newUrl);
                  if (!newUrl.trim()) {
                    setUrlError("");
                  } else if (!isValidUrl(newUrl)) {
                    setUrlError(
                      "Please enter a valid URL (starting with http:// or https://)",
                    );
                  } else {
                    setUrlError("");
                  }
                }}
              />
              {urlError && <p className="file-error-text">{urlError}</p>}
            </div>
          )}
          {sourceType === "azure" && (
            <div className="popup-form-group">
              <label>Upload Video</label>
              <div
                className={`drop-area ${dragActive ? "drag-active" : ""}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="video/*"
                  onChange={handleFileChange}
                />

                {!file ? (
                  <div className="upload-ui">
                    <img src={dragIcon} alt="upload" width={48} />
                    <p>Drag & drop or Browse</p>
                  </div>
                ) : (
                  <div className="file-row">
                    <span>{file.name}</span>
                    <button type="button" onClick={removeFile}>
                      ×
                    </button>
                  </div>
                )}
              </div>
              {mode === "edit" && video?.video_url && (
                <p className="existing-url-text">
                  Existing URL:{" "}
                  <a href={video.video_url} target="_blank" rel="noreferrer">
                    Click to view
                  </a>
                </p>
              )}
              {fileError && <p className="file-error-text">{fileError}</p>}
            </div>
          )}
          <div className="popup-actions">
            <button
              type="submit"
              className="popup-submit-button"
              disabled={submitting}
            >
              {mode === "edit" ? "Save Changes" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default CreateVideoPopup;
